/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Stack,
  Tooltip,
  IconButton,
  Button,
  Typography,
  SvgIconProps,
  Chip,
} from "@mui/material";
import React, { useCallback, useMemo } from "react";
import {
  Edit,
  PersonAddAltRounded,
  Visibility,
  ContentCopy,
  Close,
  CheckCircle,
  History,
} from "@mui/icons-material";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { CustomDatePicker } from "../../components/formsComponents/CustomDatePicker";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { CustomChip } from "../../components/CustomChip";
import { LeadStatusRenderer } from "../../components/LeadStatusRenderer/LeadStatusRenderer";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { StandardTableActions } from "../../components/StandardTableActions/StandardTableActions";
import { DataTableV2 } from "../../components/TableV2/DataTableV2";
import {
  IDataTableV2State,
  useTableV2State,
} from "../../components/TableV2/hooks/useTableV2State";
import {
  DataTableV2RowRenderType,
  IDataTableV2Props,
} from "../../components/TableV2/interfaces/IDataTableV2Props";
import { PageContainer } from "../../components/container/PageContainer";
import {
  CustomFormLabel,
  DebouncedTextField,
} from "../../components/formsComponents";
import {
  SEARCH_FIELDS_MAX_WIDTH_SX,
  STANDARD_APP_DATE_FORMAT,
  TABLE_CACHING_KEYS,
} from "../../constants/constants";
import { LoadState, MODULE_NAMES } from "../../constants/enums";
import { useDispatchWrapper } from "../../hooks";
import { Breadcrumb } from "../../layout/fullLayout/breadcrumb/Breadcrumb";
import { IBreadcrumbProps } from "../../layout/fullLayout/breadcrumb/IBreadcrumbProps";
import { useBillingCompanyContext } from "../../providers";
import {
  clearActionItemState,
  fetchActionItemsListAsync,
  IActionItem,
} from "../../redux/actionItems";
import { IStoreState } from "../../redux/initialStoreState";
import {
  ILead,
  initialLead,
  initialMultipleEvent,
} from "../../redux/leads/leads.types";
import { fetchLeadAsync } from "../../redux/leads/leadsActions";

import { ViewLeadDetails } from "../leads/components/ViewLeadDeatils/ViewLeadDetails";
import { ViewHistoryDetailsRightPanel } from "../leads/LeadHistory/viewHistoryDetails";
import { EditLeadDetails } from "../dashboard/Dashboard/components/ViewLeadDeatils/EditLeadDetails";
import { DataTableV2MobileFilter } from "../../components/TableV2/components/mobileDataTableV2CommandBar/MobileFilter";
import {
  getCountryCode,
  replaceCountryCode,
} from "../../helpers/replaceConutryCode";
import { AssignChatLeadDialog } from "./dialogs/AssignChatLeadDialog";
import { ViewChatLeadDialog } from "./dialogs/ViewChatLeadDialog";
import { ChatLeadQuickEditRightPanel } from "./dialogs/ChatLeadQuickEditRightPanel";
import { REACT_APP_NAV_ROUTES } from "../../router/AppNavigation";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../redux/messages/messagesActions";
import { ViewWhatsappDetails } from "../leads/components/viewWhatsappDetails";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { DataTableV2Date } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date";
import { DataTableV2QuickSearch } from "../../components/TableV2/preDefinedPlugins/DataTableV2QuickSearch/DataTableV2QuickSearch";
import { useDefaultOngoingStatus } from "../leads/hooks/useDefaultOngoingStatus";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { CSVNew } from "../../components/TableV2/preDefinedPlugins/CSVExportV2/CSVNew";
import { api } from "../../api/api";
import { getSearchQueryV2 } from "../../helpers/queryParam";
import produce from "immer";
import { LeadSourceFilter } from "../../components/AutoCompleteSearches/LeadSourceFilter";
import { PriorityFilter } from "../../components/AutoCompleteSearches/PriorityFilter";
import { UsersAutoSearch } from "../../components/AutoCompleteSearches/UsersAutoSearch";
import { ExtLeadStatusFilter } from "./selectors/ExtLeadStatusFilter";
import { convertExtLeadToEvtLead } from "./helpers/convertExtLeadToEvtLead";
import { IDrivedExternalLeadStatus } from "../../redux/external-leads/external-leads-life-cycle/external-lead-status.types";
import { capitalize } from "lodash";
import { getStatusChipColors, IStatusChipColors } from "../../utils/colorUtils";
import {
  DialerCallButton,
  DialerCallDialog,
  useDialerCall,
} from "../leads/components/DialerCallButton";
import { useAuth } from "../../contexts/AuthProvider";
import moment from "moment";
import { useTimezoneFormatter } from "../../hooks/useTimezoneFormatter";

// Sentinel value used in the "Handled By" filter to represent the
// "Unassigned" option. When selected, the API receives a null filter on
// assigned_to_uuid (i.e. handled_by_uuid / handled_by_name are null).
const UNASSIGNED_HANDLED_BY = "__UNASSIGNED__";

const isCancelledActionItem = (row: IActionItem) =>
  (row.actual_status || "").toUpperCase() === "CANCELLED";

// Non-clickable green tick shown in place of "Create Event Lead" for cancelled
// leads. Ignores the `color` StandardTableActions passes, and overrides the
// actions bar's svg cursor:pointer so it doesn't look clickable.
const CancelledTickIcon = React.forwardRef<SVGSVGElement, SvgIconProps>(
  ({ color, ...rest }, ref) => (
    <CheckCircle
      {...rest}
      ref={ref}
      sx={{ color: "success.main", cursor: "default !important" }}
    />
  ),
) as React.ComponentType<SvgIconProps>;

const addOrRemoveFilter = (
  filters: any[],
  column: string,
  operator: string,
  value: any,
) => {
  const colKey = JSON.stringify([column]);
  const next = (filters || []).filter(
    (f) =>
      JSON.stringify(f?.column || []) !== colKey ||
      String(f?.operator || "") !== operator,
  );

  if (value === null || value === undefined || String(value).trim() === "") {
    return next;
  }

  next.push({
    column: [column],
    logicalOperator: "AND",
    operator,
    value,
  });
  return next;
};

const getAdvancedFilters = (
  searchState: any,
  otherCustomStateProperties: any,
) => {
  const filters = [...(Array.isArray(searchState) ? searchState : [])] as any[];

  if (otherCustomStateProperties?.searchByPriority) {
    filters.push({
      column: ["priority"],
      logicalOperator: "AND",
      operator: "EQUAL",
      value: otherCustomStateProperties.searchByPriority,
    });
  }
  if (otherCustomStateProperties?.searchBySource) {
    filters.push({
      column: ["source"],
      logicalOperator: "AND",
      operator: "CONTAINS",
      value: otherCustomStateProperties.searchBySource,
    });
  }
  if (otherCustomStateProperties?.searchByHandledBy) {
    const handledByVal =
      typeof otherCustomStateProperties.searchByHandledBy === "object"
        ? otherCustomStateProperties.searchByHandledBy?.user_uuid
        : otherCustomStateProperties.searchByHandledBy;
    if (handledByVal === UNASSIGNED_HANDLED_BY) {
      // "Unassigned" → match records with no assigned employee (null).
      filters.push({
        column: ["assigned_to_uuid"],
        logicalOperator: "AND",
        operator: "IS_NULL",
        value: null,
      });
    } else if (handledByVal) {
      filters.push({
        column: ["assigned_to_uuid"],
        logicalOperator: "AND",
        operator: "EQUAL",
        value: handledByVal,
      });
    }
  }
  if (otherCustomStateProperties?.searchByCategory) {
    filters.push({
      column: ["category_name"],
      logicalOperator: "AND",
      operator: "CONTAINS",
      value: otherCustomStateProperties.searchByCategory,
    });
  }
  if (otherCustomStateProperties?.searchByNameOrNumber) {
    filters.push({
      column: ["name", "mobile_no"],
      logicalOperator: "OR",
      operator: "CONTAINS",
      value: otherCustomStateProperties.searchByNameOrNumber,
    });
  }
  if (otherCustomStateProperties?.followupDate) {
    filters.push({
      column: ["followup_date"],
      logicalOperator: "AND",
      operator: "EQUAL",
      value: otherCustomStateProperties.followupDate,
    });
  }

  const finalSearchByStatus = otherCustomStateProperties?.searchByStatus || [];
  if (finalSearchByStatus.includes("ALL")) {
    // "ALL" → no status filter is applied to the API query.
  } else if (finalSearchByStatus.includes("ALL_EXCEPT_JUNKS")) {
    filters.push({
      column: ["status"],
      logicalOperator: "AND",
      operator: "NOT_IN",
      value: ["JUNK"],
    });
  } else if (finalSearchByStatus.includes("ALL_EXCEPT_DEAD")) {
    filters.push({
      column: ["status"],
      logicalOperator: "AND",
      operator: "NOT_IN",
      value: ["DEAD"],
    });
  } else if (finalSearchByStatus.includes("ALL_ACTIVE")) {
    filters.push({
      column: ["actual_status"],
      logicalOperator: "AND",
      // operator: "NOT_IN",
      // value: ["DEAD", "CANCELLED", "LEAD"],
      // Krishna requested to change from above to following on 21-08-26
      operator: "EQUAL",
      value: "ONGOING",
    });
  } else if (finalSearchByStatus.length > 0) {
    filters.push({
      column: ["status"],
      logicalOperator: "AND",
      operator: "IN",
      value: finalSearchByStatus,
    });
  }

  return filters;
};

export const ChatLeads: React.FC<{
  statusParam?: string;
  hideFilters?: boolean;
}> = (props) => {
  const { country } = useAuth();
  const dispatch = useDispatchWrapper();
  const companyOutlet = useBillingCompanyContext();
  const defaultOngoingStatus = useDefaultOngoingStatus();
  const navigate = useNavigate();
  const { dialerState, initiateCall, closeDialog } = useDialerCall();
  const { ftDateTime, ftDate } = useTimezoneFormatter();

  // Status name -> colour, fetched from the same nested category/drive-status
  // endpoint the External Lead Status Lifecycle admin page uses, so the chip
  // colour here always matches what's configured there — including the
  // category's own colour as a fallback for statuses with no child colour set.
  // Light sub-status colours become the chip background with master-coloured
  // text (see getStatusChipColors).
  const [extStatusColorMap, setExtStatusColorMap] = React.useState<
    Record<string, IStatusChipColors>
  >({});

  React.useEffect(() => {
    const fetchExtStatusColors = async () => {
      if (!companyOutlet.comapny.company_outlet_uuid) return;
      try {
        const res = await api.get(
          `/dataManagement/get-drive-external-status-list?company_outlet_uuid=${companyOutlet.comapny.company_outlet_uuid}`,
        );
        const categories: any[] = res.data.data || [];
        const map: Record<string, IStatusChipColors> = {};
        categories.forEach((category) => {
          const categoryColor = category.label_colour;
          const categoryColors = getStatusChipColors(
            categoryColor,
            categoryColor,
          );
          if (category.master_external_status_name && categoryColors) {
            map[category.master_external_status_name.toUpperCase()] =
              categoryColors;
          }
          (category.drive_status || []).forEach(
            (ds: IDrivedExternalLeadStatus) => {
              const colors = getStatusChipColors(
                ds?.label_colour || categoryColor,
                (ds as any)?.master_label_colour || categoryColor,
              );
              if (ds?.name && colors) {
                map[ds.name.toUpperCase()] = colors;
              }
            },
          );
        });
        setExtStatusColorMap(map);
      } catch {
        // Colours are a visual enhancement only — fall back silently to the
        // default LeadStatusRenderer colours on failure.
      }
    };
    fetchExtStatusColors();
  }, [companyOutlet.comapny.company_outlet_uuid]);

  const renderStatusChip = React.useCallback(
    (row: IActionItem) => {
      const colors =
        extStatusColorMap[(row.status || "").toUpperCase()] ||
        extStatusColorMap[(row.actual_status || "").toUpperCase()];
      if (!colors) {
        return (
          <LeadStatusRenderer
            status={row.status || ""}
            actualStatus={row.actual_status || ""}
          />
        );
      }
      return (
        <CustomChip
          color="primary"
          content={row.status ? capitalize(row.status) : "--"}
          size="small"
          sx={{
            color: colors.text,
            bgcolor: colors.bg,
            borderColor: colors.border,
            "& .MuiChip-label": { color: colors.text },
          }}
        />
      );
    },
    [extStatusColorMap],
  );

  const handleCopyPhoneNumber = useCallback(
    async (phoneNumber: string) => {
      try {
        await navigator.clipboard.writeText(phoneNumber);
        dispatch(
          showMessage({
            type: "success",
            displayAs: "snackbar",
            message: "Phone number copied!",
          }),
        );
      } catch {
        dispatch(
          showMessage({
            type: "error",
            displayAs: "snackbar",
            message: "Failed to copy phone number",
          }),
        );
      }
    },
    [dispatch],
  );

  const BCrumb: IBreadcrumbProps["items"] = [
    {
      to: "/dashbaord",
      title: "dashboard",
    },
    {
      title: "External Leads",
    },
  ];

  const {
    list: reduxList,
    totalRecords: reduxTotalRecords,
    loading: reduxLoading,
  } = useSelector((storeState: IStoreState) => storeState.actionItems.list);

  const [localRows, setLocalRows] = React.useState<IActionItem[]>([]);
  const [localTotalRecords, setLocalTotalRecords] = React.useState<number>(0);
  const [localLoading, setLocalLoading] = React.useState<LoadState>(
    LoadState.NotLoaded,
  );

  const isExternal = Boolean(props.statusParam);

  const list = isExternal ? localRows : reduxList;
  const totalRecords = isExternal ? localTotalRecords : reduxTotalRecords;
  const loading = isExternal ? (localLoading as any) : reduxLoading;

  const [openView, setOpenView] = React.useState<ILead | null>(null);
  const [openViewChatLead, setOpenViewChatLead] =
    React.useState<IActionItem | null>(null);
  const [openEdit, setOpenEdit] = React.useState<{
    lead: ILead | null;
    chatActionItem: IActionItem | null;
  }>({
    lead: null,
    chatActionItem: null,
  });

  const [openAssignChatLead, setOpenAssignChatLead] =
    React.useState<IActionItem | null>(null);

  const [openQuickEdit, setOpenQuickEdit] = React.useState<IActionItem | null>(
    null,
  );

  const [openWhatsapp, setOpenWhatsapp] = React.useState<IActionItem | null>(
    null,
  );

  const [openHistory, setOpenHistory] = React.useState<IActionItem | null>(
    null,
  );

  const {
    state: {
      dateState,
      searchState,
      tabs,
      columnsConfig,
      pagination,
      masterColumnResize,
      otherCustomStateProperties,
    },
    setDateState,
    resetState,
    state,
    setSearchState,
    setColumnVisibility,
    onPageChange,
    onRowsPerPageChange,
    onResizeMasterColumns,
    onMobileStateChange,
    initialColumnVisibility,
  } = useTableV2State({
    filtersInitialState: {
      defaultDateRange: "last30Days",
    },
    columnsConfig: {
      columnVisibility: {
        description: { visible: false, order: 9 },
      },
    },
    pagination: {
      pageNumber: 1,
      rowsInPerPage: 25,
      apiFetchRowCount: 26,
      extraFetchFactor: 1,
    },
    cachingFilters: {
      uniqueKey: TABLE_CACHING_KEYS.CHAT_LEADS,
    },
    otherCustomStateProperties: {
      searchByConversationId: "",
      searchByNameOrNumber: "",
      followupDate: null,
      searchByHandledBy: { user_uuid: "", user_name: "" },
      searchByCategory: "",
      searchBySource: "",
      searchByPriority: "",
      searchByStatus: ["ALL_ACTIVE"],
    },
  });

  const ALL_EXCEPT_JUNKS_STATUSES = [
    "ONGOING",
    "BOOKED",
    "DEAD",
    "POSTPONED",
    "HOLD",
  ];

  const isFilterApplied = useMemo(() => {
    const handledByVal =
      typeof otherCustomStateProperties.searchByHandledBy === "object"
        ? otherCustomStateProperties.searchByHandledBy?.user_uuid
        : otherCustomStateProperties.searchByHandledBy;
    return Boolean(
      otherCustomStateProperties.searchByConversationId ||
      otherCustomStateProperties.searchByNameOrNumber ||
      otherCustomStateProperties.followupDate ||
      handledByVal ||
      otherCustomStateProperties.searchByCategory ||
      (otherCustomStateProperties.searchByPriority &&
        otherCustomStateProperties.searchByPriority !== "") ||
      otherCustomStateProperties.searchBySource ||
      (otherCustomStateProperties.searchByStatus?.length > 0 &&
        !otherCustomStateProperties.searchByStatus.includes("ALL_ACTIVE") &&
        !otherCustomStateProperties.searchByStatus.includes("ALL")) ||
      dateState.rangeType !== "last30Days",
    );
  }, [otherCustomStateProperties, dateState.rangeType]);

  const convertActionItemIntoLeadDetails = (actionItem: IActionItem): ILead => {
    const ongoingName = defaultOngoingStatus?.name || "ONGOING";
    const ongoingDriveStatusUuid =
      defaultOngoingStatus?.drive_status_uuid || "";
    return {
      ...initialLead,
      reference_from_action_item_uuid: actionItem.action_item_uuid,
      name_of_guest: actionItem.name,
      contact_number_one: replaceCountryCode(actionItem.mobile_no),
      country_code: getCountryCode(actionItem.mobile_no),
      remarks: actionItem.description,
      date_of_visit: actionItem.create_ts,
      source: actionItem.source || "Direct",
      handled_by_employee_uuid: actionItem.handled_by_employee_uuid as string,
      handled_by_employee: actionItem.handled_by_employee as string,
      lead_source_uuid: actionItem.lead_source_uuid ?? "",
      priority: actionItem.priority || "HOT",
      multiple_events_list: [
        {
          ...initialMultipleEvent,
          status: ongoingName,
          drive_status_uuid: ongoingDriveStatusUuid,
          event_time_uuid: actionItem.event_time_uuid as string,
          event_time: actionItem.event_time as string,
          event_type: actionItem.event_type as string,
          followup_date: actionItem.followup_date as string,
          function_date: actionItem.function_date as string,
          function_month: actionItem.function_month as string,
          guest_budget: actionItem.guest_budget as string,
          number_of_guests: actionItem.number_of_guests as number,
          number_of_rooms: actionItem.number_of_rooms as number,
          preferred_venue_uuid: actionItem.preferred_venue_uuid as string,
          preferred_venue: actionItem.preferred_venue as string,
        },
      ],
    };
  };

  const handleEditClick = React.useCallback(
    async (row: IActionItem): Promise<void> => {
      // If status is "LEAD", try to fetch the actual lead data
      if (row.status === "LEAD") {
        // Check if action item has leads_uuid (might be in API response but not in type)
        const leadsUuid = (row as any).leads_uuid;

        if (leadsUuid) {
          dispatch(
            fetchLeadAsync({
              leadId: leadsUuid,
              company_outlet_uuid: companyOutlet.comapny.company_outlet_uuid,
              onCallBack: (lead, isSuccess) => {
                if (isSuccess && lead) {
                  setOpenEdit({
                    lead: lead,
                    chatActionItem: row,
                  });
                }
              },
            }),
          );
          return;
        } else {
          dispatch(
            showMessage({
              displayAs: "snackbar",
              message: "No leads_uuid found for this action item.",
              type: "error",
            }),
          );
          return;
        }
      }
    },
    [dispatch, companyOutlet.comapny.company_outlet_uuid],
  );

  const fetchAllData = async () => {
    if (isExternal) {
      if (!companyOutlet.comapny.company_outlet_uuid) return [];
      try {
        let url =
          `/leads/get-status-chat-external-leads?type=list` +
          `&pageNo=1&pageLimit=10000&itemPerPage=10000` +
          `&company_outlet_uuid=${companyOutlet.comapny.company_outlet_uuid}`;

        if (props.statusParam && props.statusParam !== "-1") {
          url += `&status_type=${encodeURIComponent(props.statusParam)}`;
        }

        const res = await api.get(url);
        return (
          res.data?.data?.leadList ||
          res.data?.data?.list ||
          res.data?.data ||
          []
        );
      } catch {
        return [];
      }
    }

    try {
      const merged7 = getAdvancedFilters(
        searchState,
        otherCustomStateProperties,
      );

      const searchQuery = getSearchQueryV2({
        // page: pagination.pageNumber,
        // rowsPerPage: pagination.rowsInPerPage,
        // limit: pagination.apiFetchRowCount,
        isDownloading: true,
        date: dateState.dates,
        advancedFilters: merged7,
      });

      let param = "";
      if (otherCustomStateProperties?.searchByConversationId) {
        param += `&mobile_no=${otherCustomStateProperties?.searchByConversationId}`;
      }

      if (companyOutlet.comapny.company_outlet_uuid) {
        param += `&company_outlet_uuid=${companyOutlet.comapny.company_outlet_uuid}`;
      }

      let url = `/conversation/get-action-item?${searchQuery}${param}`;

      const res = await api.get(url);
      return res.data?.data || [];
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
      return [];
    }
  };

  const handleExportStart = useCallback(() => {
    dispatch(saveLoaderProgress());
  }, [dispatch]);

  const handleExportComplete = useCallback(() => {
    dispatch(saveLoaderCompleted());
  }, [dispatch]);

  const handleCustomFilterChange = (key: string, value: any) => {
    const newState = produce(state, (draftState) => {
      draftState.otherCustomStateProperties[key] = value;
      // Applying any OTHER filter forces the status to "All" (no status filter),
      // so results aren't unexpectedly narrowed by the default status.
      if (key !== "searchByStatus") {
        draftState.otherCustomStateProperties.searchByStatus = ["ALL"];
      }
      draftState.pagination.pageNumber = 1;
    });
    setFilterStateSync(newState);
  };
  const setFilterStateSync = React.useCallback(
    (value: React.SetStateAction<IDataTableV2State>) => {
      const prev = state;
      const resolved =
        typeof value === "function" ? (value as any)(prev) : value;

      const isSameDateState =
        resolved.dateState?.rangeType === "allTimes" &&
        !resolved.dateState?.dates?.fromDate &&
        !resolved.dateState?.dates?.toDate;

      const next = isSameDateState
        ? resolved
        : {
          ...resolved,
          dateState: {
            rangeType: "allTimes",
            dates: { fromDate: "", toDate: "" },
          },
        };
      // Persist to cache/server (was raw setState before, which never saved the
      // custom filters like status/source/follow-up/handled-by/priority).
      onMobileStateChange(next);
    },
    [state, onMobileStateChange],
  );

  const leadsTableProps: IDataTableV2Props = {
    isPagination: true,
    totalRecords: totalRecords,
    pageNumber: pagination.pageNumber,
    rowsPerPageOptions: pagination.rowsInPerPage,
    isDataLoading: loading === LoadState.InProgress,
    extraFetchFactor: pagination.extraFetchFactor,

    tableCommandBarProps: {
      customCommandBar: props.hideFilters ? undefined : (
        <Box
          sx={{
            display: "flex",
            width: "100%",
            gap: 1,
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              flex: 1,
              minWidth: 0,
            }}
          >
            <DataTableV2Date
              state={dateState}
              onChange={setDateState}
              isHighlighted={dateState.rangeType !== "last30Days"}
              sx={SEARCH_FIELDS_MAX_WIDTH_SX}
            />

            <Box
              sx={{
                ...SEARCH_FIELDS_MAX_WIDTH_SX,
                minWidth: 175,
                maxWidth: 280,
                flex: "1.5 1 190px",
              }}
            >
              {/* <MasterStatusMultiSelectAutoSearch
                value={otherCustomStateProperties["searchByStatus"]}
                extraOptions={[{ label: "ALL EXCEPT JUNK", value: "ALL_EXCEPT_JUNKS" }]}
                onChange={(newValue) => {
                  handleCustomFilterChange("searchByStatus", newValue)
                }}
                isHighlighted={otherCustomStateProperties["searchByStatus"]?.length > 0 && !otherCustomStateProperties["searchByStatus"].includes("ALL_EXCEPT_JUNKS")}
              /> */}
              <ExtLeadStatusFilter
                value={otherCustomStateProperties["searchByStatus"]}
                extraOptions={[
                  { label: "ALL", value: "ALL" },
                  { label: "ALL ACTIVE", value: "ALL_ACTIVE" },
                  { label: "ALL EXCEPT JUNK", value: "ALL_EXCEPT_JUNKS" },
                  { label: "ALL EXCEPT DEAD", value: "ALL_EXCEPT_DEAD" },
                ]}
                onChange={(newValue) => {
                  const oldValues =
                    otherCustomStateProperties["searchByStatus"];

                  const hadAll = oldValues?.includes("ALL");
                  const nowHasAll = newValue.includes("ALL");
                  const hadAllExcept = oldValues?.includes("ALL_EXCEPT_JUNKS");
                  const nowHasAllExcept = newValue.includes("ALL_EXCEPT_JUNKS");
                  const hadAllExceptDead =
                    oldValues?.includes("ALL_EXCEPT_DEAD");
                  const nowHasAllExceptDead =
                    newValue.includes("ALL_EXCEPT_DEAD");
                  const hadAllActive = oldValues?.includes("ALL_ACTIVE");
                  const nowHasAllActive = newValue.includes("ALL_ACTIVE");

                  if (nowHasAll && !hadAll) {
                    handleCustomFilterChange("searchByStatus", ["ALL"]);
                  } else if (nowHasAllExcept && !hadAllExcept) {
                    handleCustomFilterChange("searchByStatus", [
                      "ALL_EXCEPT_JUNKS",
                    ]);
                  } else if (nowHasAllExceptDead && !hadAllExceptDead) {
                    handleCustomFilterChange("searchByStatus", [
                      "ALL_EXCEPT_DEAD",
                    ]);
                  } else if (nowHasAllActive && !hadAllActive) {
                    handleCustomFilterChange("searchByStatus", ["ALL_ACTIVE"]);
                  } else if (
                    (hadAll ||
                      hadAllExcept ||
                      hadAllExceptDead ||
                      hadAllActive) &&
                    newValue.length > 1
                  ) {
                    const filter = newValue.filter(
                      (f) =>
                        f !== "ALL" &&
                        f !== "ALL_EXCEPT_JUNKS" &&
                        f !== "ALL_EXCEPT_DEAD" &&
                        f !== "ALL_ACTIVE",
                    );
                    handleCustomFilterChange("searchByStatus", filter);
                  } else {
                    handleCustomFilterChange("searchByStatus", newValue);
                  }
                }}
                isHighlighted={
                  otherCustomStateProperties["searchByStatus"]?.length > 0 &&
                  !otherCustomStateProperties["searchByStatus"].includes(
                    "ALL",
                  ) &&
                  !otherCustomStateProperties["searchByStatus"].includes(
                    "ALL_EXCEPT_JUNKS",
                  ) &&
                  !otherCustomStateProperties["searchByStatus"].includes(
                    "ALL_EXCEPT_DEAD",
                  ) &&
                  !otherCustomStateProperties["searchByStatus"].includes(
                    "ALL_ACTIVE",
                  )
                }
              />
            </Box>

            <Box
              sx={{
                ...SEARCH_FIELDS_MAX_WIDTH_SX,
                minWidth: 130,
                maxWidth: 160,
                flex: "0.8 1 130px",
              }}
            >
              <LeadSourceFilter
                value={otherCustomStateProperties["searchBySource"]}
                onChange={(newValue) => {
                  handleCustomFilterChange("searchBySource", newValue);
                }}
                isHighlighted={
                  otherCustomStateProperties["searchBySource"]?.length > 0
                }
              />
            </Box>

            <Box position="relative" sx={SEARCH_FIELDS_MAX_WIDTH_SX}>
              <CustomDatePicker
                placeholder="Follow-up Date"
                value={otherCustomStateProperties.followupDate}
                onChange={(newValue) => {
                  handleCustomFilterChange("followupDate", newValue);
                }}
                fullWidth
                sx={
                  otherCustomStateProperties.followupDate
                    ? {
                      backgroundColor: "#fff2c7",
                      borderRadius: 1,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "warning.main",
                        borderWidth: "2px !important",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "warning.main",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "warning.main",
                      },
                    }
                    : undefined
                }
              />

              {otherCustomStateProperties.followupDate && (
                <IconButton
                  size="small"
                  onClick={() => handleCustomFilterChange("followupDate", null)}
                  sx={{
                    position: "absolute",
                    right: 36,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 1,
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              )}
            </Box>

            <Box
              sx={{
                ...SEARCH_FIELDS_MAX_WIDTH_SX,
                minWidth: 130,
                maxWidth: 170,
                flex: "0.8 1 130px",
              }}
            >
              <UsersAutoSearch
                label="Handled By"
                placeholder="Search by Handled By"
                value={otherCustomStateProperties.searchByHandledBy}
                isHighlighted={
                  !!otherCustomStateProperties.searchByHandledBy?.user_uuid
                }
                staticOptions={[
                  {
                    user_uuid: UNASSIGNED_HANDLED_BY,
                    user_name: "Unassigned",
                  } as any,
                ]}
                onSelect={(user) => {
                  const builtName =
                    `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
                  handleCustomFilterChange("searchByHandledBy", {
                    user_uuid: user?.user_uuid || "",
                    // Fall back to user_name for static sentinel options
                    // (e.g. "Unassigned") which have no first/last name.
                    user_name: builtName || user?.user_name || "",
                  });
                }}
              />
            </Box>

            <Box
              sx={{
                ...SEARCH_FIELDS_MAX_WIDTH_SX,
                minWidth: 130,
                maxWidth: 160,
                flex: "0.8 1 130px",
              }}
            >
              <PriorityFilter
                isFilter
                value={otherCustomStateProperties.searchByPriority || ""}
                onChange={(val) =>
                  handleCustomFilterChange("searchByPriority", val)
                }
                isHighlighted={!!otherCustomStateProperties.searchByPriority}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
              marginLeft: "auto",
            }}
          >
            <Tooltip title="Clear Filters">
              <IconButton
                onClick={() => {
                  resetState();
                  // Persist the cleared filters so they survive reload/login.
                  onMobileStateChange({
                    ...state,
                    dateState: {
                      ...state.dateState,
                      rangeType: "last30Days",
                    },
                    otherCustomStateProperties: {
                      ...state.otherCustomStateProperties,
                      searchByConversationId: "",
                      searchByNameOrNumber: "",
                      followupDate: null,
                      searchByHandledBy: { user_uuid: "", user_name: "" },
                      searchByCategory: "",
                      searchBySource: "",
                      searchByPriority: "",
                      searchByStatus: ["ALL_ACTIVE"],
                    },
                    pagination: { ...state.pagination, pageNumber: 1 },
                  });
                }}
                size="small"
                sx={{
                  borderRadius: 1,
                  py: "6px",
                  ...(isFilterApplied
                    ? {
                      background: (theme) => theme.palette.warning.main,
                      "&:hover": {
                        background: "#ffa20f",
                      },
                    }
                    : {
                      background: "transparent",
                      border: "1px solid #e0e0e0",
                    }),
                }}
              >
                <FilterAltOffIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      ),
      preDefinedPlugins: props.hideFilters
        ? {
          refresh: {
            onClick: () => {
              fetchList();
            },
          },
          autoFit: true,
        }
        : {
          search: {
            tableName: "latest_action_item",
            excludeColumnsInSearch: ["view", "actions"],
            state: searchState,
            onChange: setSearchState,
            loadInitialFilterOncePopoverOpened: [
              {
                column: ["name"],
                logicalOperator: "AND",
                operator: "CONTAINS",
                value: "",
              },
            ],
          },
          columnVisibility: {
            columnVisibility: columnsConfig.columnVisibility,
            initialColumnVisibility,
            onChange: setColumnVisibility,
          },
          refresh: {
            onClick: () => {
              fetchList();
            },
          },
          autoFit: true,
        },
      leftItems: {},
      centerItems: {
        customPlugins: [
          {
            key: "searchByNameOrNumber",
            onRender: () => (
              <DataTableV2QuickSearch
                value={otherCustomStateProperties.searchByNameOrNumber}
                onChange={(val) =>
                  handleCustomFilterChange("searchByNameOrNumber", val)
                }
                placeholder="Search by Name or Number"
                delay={600}
              />
            ),
          },
        ],
      },
      rightItems: {
        customPlugins: [
          {
            key: "csvExport",
            onRender: (columns, items) => (
              <CSVNew
                columns={[
                  {
                    columnName: "Status",
                    fieldName: "status",
                    width: 20,
                  },
                  {
                    columnName: "Priority",
                    fieldName: "priority",
                    width: 20,
                  },
                  {
                    columnName: "Source",
                    fieldName: "source",
                    width: 20,
                  },
                  {
                    columnName: "Category",
                    fieldName: "category_name",
                    width: 20,
                  },
                  {
                    columnName: "Name",
                    fieldName: "name",
                    width: 20,
                  },
                  {
                    columnName: "Contact No",
                    fieldName: "mobile_no",
                    width: 20,
                  },
                  {
                    columnName: "Followup Date",
                    fieldName: "followup_date",
                    width: 20,
                  },
                  {
                    columnName: "Handled by",
                    fieldName: "assigned_to_name",
                    width: 20,
                  },
                  {
                    columnName: "Action Item",
                    fieldName: "item",
                    width: 20,
                  },
                  {
                    columnName: "Description",
                    fieldName: "description",
                    width: 20,
                  },
                  {
                    columnName: "Created At",
                    fieldName: "create_ts",
                    width: 20,
                  },
                ]}
                columnVisibility={columnsConfig.columnVisibility}
                enableColumnSelection={true}
                rows={list}
                onExportButtonClick={fetchAllData}
                onExportStart={handleExportStart}
                onExportComplete={handleExportComplete}
                filePrefixName="External Leads Report"
              />
            ),
          },
          ...(!props.hideFilters
            ? [
              {
                key: "createLead",
                onRender: (columns: any, items: any) => (
                  <Button
                    sx={{ ml: 1 }}
                    color="success"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      navigate(
                        `${REACT_APP_NAV_ROUTES.leads.chatLeads}/manage`,
                      );
                    }}
                  >
                    Create External Lead
                  </Button>
                ),
              },
            ]
            : []),
        ],
      },
    },

    onSeeAllDetails: (row: IActionItem) => {
      navigate(
        `${REACT_APP_NAV_ROUTES.leads.chatLeads}/manage/${row.action_item_uuid}`,
      );
    },

    mobileCommandBarProps: props.hideFilters
      ? {
        filterComponent: <></>,
      }
      : {
        filterComponent: (
          <DataTableV2MobileFilter
            state={state}
            onApply={(newState) => onMobileStateChange(newState)}
            onClear={resetState}
          >
            {(filterLocalState, setFilterlocalState) => {
              return (
                <Stack direction={"column"} spacing={1}>
                  <Box>
                    <CustomFormLabel>Select Duration</CustomFormLabel>

                    <DataTableV2Date
                      state={filterLocalState.dateState}
                      onChange={(newDateState) => {
                        setFilterlocalState({
                          ...filterLocalState,
                          dateState: newDateState,
                        });
                      }}
                      isHighlighted={
                        filterLocalState.dateState.rangeType !== "last30Days"
                      }
                    />
                  </Box>
                </Stack>
              );
            }}
          </DataTableV2MobileFilter>
        ),
      },

    mobileColumns: [
      {
        variant: "template1",
        topBanner: {
          leftColumn: {
            key: "preferred_venue",
            fieldName: "preferred_venue",
            onRowCellRender: (_value: any, row: IActionItem) => {
              const displayVenue = row.preferred_venue;
              return (
                <Chip
                  label={displayVenue || "No Venue"}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.685rem",
                    fontWeight: 700,
                    bgcolor: displayVenue ? "#e0f2fe" : "#f1f5f9",
                    color: displayVenue ? "#0369a1" : "#64748b",
                    borderRadius: "6px",
                    maxWidth: 160,
                  }}
                />
              );
            },
          },
          rightColumn: {
            key: "event_detail",
            fieldName: "event_time",
            onRowCellRender: (_value: any, row: IActionItem) => {
              const eventTime = row.event_time || "";
              const eventName = row.other_event_name || row.event_type || "";
              const eventDetail = [eventTime, eventName]
                .filter(Boolean)
                .join(" | ");

              return (
                <Typography
                  variant="caption"
                  sx={{
                    color: "#64748b",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                  }}
                >
                  {eventDetail || ""}
                </Typography>
              );
            },
          },
        },
        headingColumn: {
          key: "name",
          fieldName: "name",
          onRowCellRender: (_value: any, row: IActionItem) => (
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{ color: "#0f172a", fontSize: "0.95rem", lineHeight: 1.25 }}
            >
              {row.name || "Lead"}
            </Typography>
          ),
        },
        rightHeadingColumn: {
          key: "status",
          fieldName: "status",
          onRowCellRender: () => null,
        },
        subSectionColumn: [
          {
            key: "contact_number_one",
            fieldName: "mobile_no",
            onRowCellRender: (_value: any, row: IActionItem) => (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  mt: 0.25,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <DialerCallButton
                    phoneNumber={row.mobile_no}
                    onCall={initiateCall}
                    disabled={dialerState.loading}
                  />
                  <WhatsAppIcon
                    sx={{
                      cursor: "pointer",
                      color: "#25D366",
                      fontSize: { xs: "1.1rem", sm: "1.2rem" },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenWhatsapp(row as any);
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#1e293b",
                      fontSize: "0.85rem",
                      fontWeight: 600,
                    }}
                  >
                    {row.country_code}
                    {row.mobile_no}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <CustomChip
                    color={
                      row.priority?.toUpperCase() === "HOT"
                        ? "error"
                        : row.priority?.toUpperCase() === "WARM"
                          ? "warning"
                          : "info"
                    }
                    content={row.priority || "COLD"}
                    size="small"
                  />
                  {renderStatusChip(row as any)}
                </Box>
              </Box>
            ),
          },
          {
            key: "assigned_and_followup",
            fieldName: "followup_date",
            onRowCellRender: (_value: any, row: IActionItem) => {
              const assignedUser =
                row.assigned_to_name || row.handled_by_employee;
              const followupStr = row.followup_date
                ? moment(row.followup_date).format("DD-MMM-YYYY")
                : null;

              return (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    mt: 0.25,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#64748b",
                      fontSize: "0.775rem",
                      fontWeight: 500,
                    }}
                  >
                    {assignedUser ? `Assigned: ${assignedUser}` : ""}
                  </Typography>

                  {followupStr && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#0284c7",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                      }}
                    >
                      Follow up: {followupStr}
                    </Typography>
                  )}
                </Box>
              );
            },
          },
        ],
        otherColumns: [
          {
            key: "category_name",
            fieldName: "category_name",
            headerName: "Category",
          },
          {
            key: "description",
            fieldName: "description",
            headerName: "Description",
          },
          {
            key: "create_ts",
            fieldName: "create_ts",
            headerName: "Created At",
          },
          {
            key: "followup_date",
            fieldName: "followup_date",
            headerName: "Follow-up Date",
            onRowCellRender: (value, row: IActionItem) => {
              if (
                row.actual_status &&
                ["CANCELLED", "DEAD"].includes(row.actual_status)
              )
                return "--";
              if (row.followup_date) {
                const finalDate = ftDate(row.followup_date);
                const hasMatched =
                  finalDate === moment().format(STANDARD_APP_DATE_FORMAT);
                return (
                  <Typography sx={{ color: hasMatched ? "error.main" : "" }}>
                    {finalDate}
                  </Typography>
                );
              }
              return "--";
            },
          },
        ],
        actions: [
          {
            name: "Whatsapp",
            icon: <WhatsAppIcon />,
            click: (rowData: IActionItem) => setOpenWhatsapp(rowData),
          },
          {
            name: "Quick Edit",
            icon: <EditNoteIcon />,
            click: (rowData: IActionItem) => setOpenQuickEdit(rowData),
          },
          {
            name: "Create Event Lead",
            icon: <AddIcon />,
            click: async (rowData: any) => {
              const actionItem = rowData as IActionItem;
              const { event_lead, ext_lead } = await convertExtLeadToEvtLead(actionItem, companyOutlet.comapny.company_outlet_uuid);
              setOpenEdit({
                lead: event_lead,
                chatActionItem: ext_lead,
              });
            },
            hide: (rowData: any) =>
              isCancelledActionItem(rowData as IActionItem),
          },
          {
            // Shown in place of "Create Event Lead" for cancelled leads.
            name: "Cancelled",
            icon: <CheckCircle sx={{ color: "success.main" }} />,
            click: () => { },
            disabled: () => true,
            hide: (rowData: any) =>
              !isCancelledActionItem(rowData as IActionItem),
          },
          {
            name: "History",
            icon: <History />,
            click: (rowData: IActionItem) => setOpenHistory(rowData),
          },
        ],
      },
    ],

    masterColumns: [
      {
        key: "actions",
        headerName: "Actions",
        fieldName: "",
        width: "140px",
        isFirstColumnSticky: true,
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IActionItem) => (
          <Box sx={{ display: "flex", gap: 2 }}>
            <StandardTableActions
              onWhatsappClick={() => setOpenWhatsapp(row)}
              onQuickEditClick={() => setOpenQuickEdit(row)}
              onViewHistoryClick={() =>
                setOpenHistory(row as any as IActionItem)
              }
              otherActions={[
                {
                  icon: AddIcon,
                  tooltip: "Create Event Lead",
                  hide: isCancelledActionItem(row),
                  onClick: async () => {
                    const { event_lead, ext_lead } =
                      await convertExtLeadToEvtLead(
                        row,
                        companyOutlet.comapny.company_outlet_uuid,
                      );
                    setOpenEdit({
                      lead: event_lead,
                      chatActionItem: ext_lead,
                    });
                  },
                },
                {
                  icon: CancelledTickIcon,
                  tooltip: "Cancelled",
                  hide: !isCancelledActionItem(row),
                  disabled: true,
                  onClick: () => { },
                },
              ]}
              more={{
                title: "more",
                menuItems: [
                  {
                    icon: <Edit />,
                    label: "Edit",
                    onClick: () =>
                      navigate(
                        `${REACT_APP_NAV_ROUTES.leads.chatLeads}/manage/${row.action_item_uuid}`,
                      ),
                  },
                  {
                    icon: <EditIcon />,
                    label: "Edit Event Lead",
                    hide: row.status !== "LEAD",
                    onClick: () => handleEditClick(row),
                  },
                  {
                    icon: <Visibility />,
                    label: "View ",
                    onClick: () => setOpenViewChatLead(row),
                  },
                ],
              }}
            />
          </Box>
        ),
      },

      {
        key: "status",
        headerName: "Status",
        fieldName: "status",
        enableSorting: true,
        width: "120px",
        minWidth: 100,
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IActionItem) => renderStatusChip(row),
      },

      {
        key: "priority",
        headerName: "Priority",
        fieldName: "priority",
        enableSorting: true,
        width: "120px",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row) => {
          return (
            <CustomChip
              color={row.priority === "HOT" ? "error" : "info"}
              content={row.priority}
              size="small"
            />
          );
        },
      },
      {
        key: "source",
        headerName: "Source",
        fieldName: "source",
        width: "170px",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.TEXT,
      },
      {
        key: "category_name",
        headerName: "Category",
        fieldName: "category_name",
        width: "200px",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.TEXT,
      },
      {
        key: "name",
        headerName: "Name",
        fieldName: "name",
        width: "170px",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.TEXT,
      },
      {
        key: "mobile_no",
        headerName: "Contact No",
        fieldName: "mobile_no",
        type: "phone",
        width: "170px",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IActionItem) => {
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DialerCallButton
                phoneNumber={row.mobile_no}
                onCall={initiateCall}
                disabled={dialerState.loading}
              />
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyPhoneNumber(row.mobile_no);
                }}
                sx={{ p: 0.5 }}
                title="Copy phone number"
              >
                <ContentCopy fontSize="small" />
              </IconButton>
              <Typography variant="body2">{row.mobile_no}</Typography>
            </Box>
          );
        },
      },
      {
        key: "followup_date",
        headerName: "Follow-up Date",
        fieldName: "followup_date",
        type: "date",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        width: "130px",
        onRowCellRender: (value, row: ILead) => {
          if (
            row.actual_status &&
            ["CANCELLED", "DEAD"].includes(row.actual_status)
          )
            return "--";
          if (row.followup_date) {
            const finalDate = ftDate(row.followup_date);
            const hasMatched =
              finalDate === moment().format(STANDARD_APP_DATE_FORMAT);
            return (
              <Typography sx={{ color: hasMatched ? "error.main" : "" }}>
                {finalDate}
              </Typography>
            );
          }
          return "--";
        },

        enableSorting: true,
      },
      {
        key: "assigned_to_name",
        headerName: "Handled by",
        fieldName: "assigned_to_name",
        width: "170px",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.TEXT,
      },
      {
        key: "item",
        width: "200px",
        headerName: "Action Item",
        fieldName: "item",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.TEXT,
      },
      {
        key: "description",
        headerName: "Description",
        fieldName: "description",
        width: "200px",
        renderType: DataTableV2RowRenderType.DESCRIPTION,
        turncateLength: 50,
      },
      {
        key: "create_ts",
        headerName: "Created At",
        fieldName: "create_ts",
        width: "200px",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.DATE_TIME,
      },
    ],

    rows: list,

    onPageChange: onPageChange,
    onRowsPerPageChange: onRowsPerPageChange,

    masterColumnResize: {
      values: masterColumnResize,
      onResize: onResizeMasterColumns,
    },
  };

  const fetchList = async () => {
    if (isExternal) {
      if (!companyOutlet.comapny.company_outlet_uuid) return;
      setLocalLoading(LoadState.InProgress);
      try {
        let url =
          `/leads/get-status-chat-external-leads?type=list` +
          `&pageNo=${pagination.pageNumber}` +
          `&pageLimit=${pagination.apiFetchRowCount}` +
          `&itemPerPage=${pagination.rowsInPerPage}` +
          `&company_outlet_uuid=${companyOutlet.comapny.company_outlet_uuid}`;

        if (props.statusParam && props.statusParam !== "-1") {
          url += `&status_type=${encodeURIComponent(props.statusParam)}`;
        }

        const res = await api.get(url);
        const listData = (res.data?.data?.leadList ||
          res.data?.data?.list ||
          res.data?.data ||
          []) as IActionItem[];
        const total = Number(res.data?.totalRecords || listData?.length || 0);

        setLocalRows(Array.isArray(listData) ? listData : []);
        setLocalTotalRecords(total);
        setLocalLoading(LoadState.Loaded);
      } catch (e) {
        setLocalRows([]);
        setLocalTotalRecords(0);
        setLocalLoading(LoadState.Failed);
      }
      return;
    }

    const merged7 = getAdvancedFilters(searchState, otherCustomStateProperties);

    dispatch(
      fetchActionItemsListAsync({
        page: pagination.pageNumber,
        rowsPerPage: pagination.rowsInPerPage,
        pageLimit: pagination.apiFetchRowCount,
        date: dateState.dates,
        advanceFilter: merged7,
        // company_outlet_uuid: companyOutlet.comapny.company_outlet_uuid,
        mobile_no:
          otherCustomStateProperties?.searchByConversationId || undefined,
      }),
    );
  };

  React.useEffect(() => {
    fetchList();
  }, [
    pagination.pageNumber,
    pagination.rowsInPerPage,
    dateState.dates,
    searchState,
    otherCustomStateProperties?.searchByConversationId,
    otherCustomStateProperties?.searchByNameOrNumber,
    otherCustomStateProperties?.followupDate,
    otherCustomStateProperties?.searchByHandledBy,
    otherCustomStateProperties?.searchByCategory,
    otherCustomStateProperties?.searchBySource,
    otherCustomStateProperties?.searchByPriority,
    otherCustomStateProperties?.searchByStatus,
    companyOutlet.comapny.company_outlet_uuid,
    props.statusParam,
    isExternal,
  ]);

  React.useEffect(() => {
    if (isExternal) {
      resetState();
    }
  }, [props.statusParam]);

  React.useEffect(() => {
    return () => {
      dispatch(clearActionItemState());
    };
  }, []);

  const handleClose = () => {
    setOpenWhatsapp(null);
  };

  const handleEditClose = () => {
    setOpenEdit({
      lead: null,
      chatActionItem: null,
    });
  };

  const handleEditComplete = () => {
    setOpenEdit({
      lead: null,
      chatActionItem: null,
    });
    fetchList();
  };

  const handleCloseHistory = () => {
    setOpenHistory(null);
  };

  const tableContent = (
    <>
      <DataTableV2 stickyHeader {...leadsTableProps} />
      {openView && (
        <ViewLeadDetails
          open={true}
          lead={openView}
          onClose={() => setOpenView(null)}
        />
      )}

      {openWhatsapp && (
        <ViewWhatsappDetails
          open={true}
          onClose={() => handleClose()}
          actionItemDetails={openWhatsapp}
        />
      )}

      <DialerCallDialog
        open={dialerState.open}
        type={dialerState.type}
        message={dialerState.message}
        onClose={closeDialog}
      />

      {openHistory && (
        <ViewHistoryDetailsRightPanel
          open={true}
          onClose={() => handleCloseHistory()}
          moduleInformation={{
            moduleId: openHistory.action_item_uuid,
            moduleName: MODULE_NAMES.CHAT_LEAD,
          }}
          initialLeadDetails={openHistory}
        />
      )}

      {openEdit.lead && (
        <EditLeadDetails
          open={true}
          title="External Lead Info"
          leadDetails={openEdit.lead}
          onClose={() => handleEditClose()}
          onComplete={(data: ILead) => {
            handleEditComplete();
          }}
          chatActionItem={openEdit.chatActionItem}
        />
      )}

      {openAssignChatLead && (
        <AssignChatLeadDialog
          open={true}
          onClose={() => setOpenAssignChatLead(null)}
          actionItem={openAssignChatLead}
        />
      )}
      {openViewChatLead && (
        <ViewChatLeadDialog
          open={true}
          onClose={() => setOpenViewChatLead(null)}
          actionItem={openViewChatLead}
        />
      )}

      {openQuickEdit && (
        <ChatLeadQuickEditRightPanel
          open={true}
          actionItem={openQuickEdit}
          onClose={() => setOpenQuickEdit(null)}
          onComplete={() => {
            setOpenQuickEdit(null);
            fetchList();
          }}
        />
      )}
    </>
  );

  if (props.hideFilters) {
    return tableContent;
  }

  return (
    <PageContainer title="View External Leads" description="this is innerpage">
      <Breadcrumb title="View External Leads" items={BCrumb} />
      {tableContent}
    </PageContainer>
  );
};
